/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { DataContainerManager } from './DataContainerManager';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Container, utils } from 'pixi.js-legacy';
import { LayerManager } from '@appCanvasCommon/layers/LayerManager';
import { Subject, Observable } from 'rxjs';
import { APPLICATION_EVENTS } from '@appCanvasCommon/core/ApplicationEvents';
import { TicketActionTypes } from 'src/app/app-data/actions/ticket-actions';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { FancyAction, Ticket } from '@touchplan/common';

/**
 * This class handles the mapping of ticket data to parent DataContainer.
 * Further, it exposes an RxJS Subject to push the changed DataContainer to listening Observers
 */
export class TicketMapper extends DataContainerManager {
	public firstDraw$: Subject<void> = new Subject();
	/**
	 * Instance of a EventEmitter3. Used internally by PIXI to handle events.
	 */
	public emitter: utils.EventEmitter = new utils.EventEmitter();
	/**
	 * Stores the reference of the ticket root node PIXI.Container. All tickets will be a child to this node in the scene graph.
	 */
	private _ticketParent: Container;
	/**
	 * Stores the reference to the layer manager.
	 */
	private _layerManager: LayerManager;
	private _firstTicketDraw = false;
	/** special index hidden tickets get thrown into if the don't yet exist in the canvas */
	private _startedHidden = new Map<string, string>();

	/**
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {LayerManager} layerManager instance of layer manager
	 * @param {PIXI.Container | FancyContainer} _ticketParent
	 * @param {Observable<any>} ticketObservable$
	 */
	constructor(
		canvasAngularLand: CanvasAngularLand,
		layerManager: LayerManager,
		_ticketParent: Container,
		ticketObservable$: Observable<any>
	) {
		super(canvasAngularLand);
		this._ticketParent = _ticketParent;
		this._layerManager = layerManager;
		this.observe(ticketObservable$);
	}
	/**
	 * Creates a new ticket and then calls the renderer, which renders the first ticket pass.
	 * @param ticketData The new ticket data
	 */
	private createNewTicket(ticketData) {
		const ticket = ticketData.payload;
		return this.createNode(ticketData.key, this._ticketParent, ticket);
	}
	/**
	 * Helper method which dispatches the updateData event.
	 * @param payload
	 * @param {DataContainer} dataContainer
	 * @returns TicketMapper
	 */
	private emitUpdateData(payload, dataContainer: TicketContainer) {
		if (!dataContainer) {
			return;
		}
		dataContainer.assign(payload);
		dataContainer.updateData(this.canvasAngularLand, dataContainer);
		this.emitter.emit(APPLICATION_EVENTS.UPDATE_DATA, this.canvasAngularLand, dataContainer);
		return this;
	}
	/**
	 * Updates the data container associated with the ticket data that was updated.
	 * @param ticketArray apparently items in this array will always be an object, but won't always have a payload
	 */
	public override updateData = (ticketArray) => {
		let dataContainer: TicketContainer;
		if (!this._firstTicketDraw) {
			window.perf.log('start first ticket draw');
		}
		ticketArray.forEach((ticketData) => {
			// super specific improvement for memory on initial load while tickets are set to hidden
			if (ticketData?.payload?.hidden && !this.mapping.get(ticketData.key)) {
				this._startedHidden.set(ticketData.key, ticketData.key);
				// 👀 If the ticket's updateData is hidden and has not been added to the canvas, add it to startedHidden and return immediately.
				return;
			}

			// 👀 If the ticket's updateData is not hidden but is in startedHidden, add it back to the canvas and remove it from startedHidden.
			if (this._startedHidden.has(ticketData.key)) {
				dataContainer = this.handleAddTicketAction(ticketData);
				this._startedHidden.delete(ticketData.key);
				// 👀 Since the ticket was previously hidden but is no longer, it has been added to the canvas and removed from startedHidden.
				// So return immediately, as there is no need to execute isAddAction below because it has already been added.
				if (this.canvasAngularLand.tickets.isAddAction(ticketData)) return;
			}

			// 👀 The code below to add the ticket will execute only if the ticket was not in the startedHidden list,
			// which is highly unlikely, as it could result in duplicating tickets on the canvas.
			// New ticket added. Create a new data container
			if (this.canvasAngularLand.tickets.isAddAction(ticketData)) {
				dataContainer = this.handleAddTicketAction(ticketData);
			}
			// Data removed. Dispatch destroy event and clean-up destroyed tickets
			else if (this.canvasAngularLand.tickets.isRemoveAction(ticketData)) {
				dataContainer = this.mapping.get(ticketData.key);
				if (!dataContainer) {
					return;
				}
				//this.emitter.emit(APPLICATION_EVENTS.DESTROY, dataContainer, {children: true, texture: false, baseTexture: false});
				//this._layerManager.removeGroup(ticketData.key + "_GROUP");
				this.mapping.remove(ticketData.key);
				this.emitter.emit(APPLICATION_EVENTS.DESTROY_TICKET, this.canvasAngularLand, dataContainer);
				dataContainer.destroy({ children: true, texture: false, baseTexture: false });
			}
			//the drag start event, rearrange tickets (need a special override since a selection can be done without interacting)
			else if (ticketData.type === TicketActionTypes.ticketChangedToDrag) {
				dataContainer = this.mapping.get(ticketData.key);
				dataContainer.cutInLine();
				this.emitUpdateData(ticketData.payload, dataContainer);
			}
			// Existing ticket data changed. Update assigned data and dispatch updateData event
			else {
				this.emitUpdateData(ticketData.payload, this.mapping.get(ticketData.key));
			}
		});

		if (ticketArray.length >= 1) {
			// Dispatches an event telling the main renderer to re-draw.
			this.emitter.emit(APPLICATION_EVENTS.DRAW);
		}

		if (!this._firstTicketDraw) {
			this.firstDraw$.next();
			this._firstTicketDraw = true;
			window.perf.log('end first ticket draw', { previousEventName: 'start first ticket draw' });
		}
	};
	/**
	 * Dispatches an update event on all data containers.
	 */
	public override update = (forceDirty: boolean = false) => {
		const cache = this.mapping.getCache();
		cache.forEach((dataContainer: TicketContainer) => {
			dataContainer.update(this.canvasAngularLand, dataContainer, forceDirty);

			// Emit UPDATE separately for ticketSprite. This is a temporary workaround;
			// the emitter call might be refactored or removed in the future.
			// See improvement plan: https://mocasystems.atlassian.net/browse/TP-7432
			this.emitter.emit(APPLICATION_EVENTS.UPDATE, this.canvasAngularLand, dataContainer, forceDirty);
		});
	};

	public override destroy() {
		//TODO: DO CLEAN-UP
		this._ticketParent = null;
		this._layerManager = null;
		this._startedHidden.clear();
		// debugger;
		this.emitter.emit(APPLICATION_EVENTS.DESTROY);
		super.destroy();
	}

	private handleAddTicketAction(ticketData: FancyAction<Ticket>) {
		const dataContainer: TicketContainer = this.createNewTicket(ticketData);
		dataContainer.updateData(this.canvasAngularLand, dataContainer);
		this.emitter.emit(APPLICATION_EVENTS.UPDATE_DATA, this.canvasAngularLand, dataContainer);
		return dataContainer;
	}
}
